/**
 * Prompts editor (V1 — S-07/S-25 shared): pick ≤3 from the Humble pool
 * (©prompts.pool via GET /prompts), answer inline, save via PUT /me/prompts.
 */
import { useEffect, useState } from "react";
import { copy, resolveCopy } from "@humble/shared";
import { Button, toast } from "../../design/index.js";
import { api, isApiError } from "../../lib/api.js";
import "./profile.css";

export interface PromptCatalogEntry {
  id: number;
  copy_key?: string;
  text?: string;
}

export interface PromptAnswer {
  prompt_id: number;
  answer: string;
}

export interface PromptsEditorProps {
  /** Current answers in display order (from /me profile.prompts). */
  answers: PromptAnswer[];
  /** Called after a successful save so the owner refetches. */
  onSaved: () => void;
}

const MAX_PROMPTS = 3;
const MAX_ANSWER = 300;

export function PromptsEditor({ answers, onSaved }: PromptsEditorProps) {
  const [catalog, setCatalog] = useState<PromptCatalogEntry[]>([]);
  const [rows, setRows] = useState<PromptAnswer[]>(answers);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    let mounted = true;
    void api
      .get("/prompts")
      .then((list) => {
        if (mounted && Array.isArray(list)) {
          setCatalog(list.filter((p): p is PromptCatalogEntry => typeof p.id === "number"));
        }
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    setRows(answers);
    setDirty(false);
  }, [answers]);

  function promptText(id: number): string {
    const entry = catalog.find((p) => p.id === id);
    return entry?.text ?? (entry?.copy_key ? (resolveCopy(entry.copy_key) ?? entry.copy_key) : `Prompt ${id}`);
  }

  function updateRow(index: number, patch: Partial<PromptAnswer>) {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
    setDirty(true);
  }

  function addRow() {
    const used = new Set(rows.map((r) => r.prompt_id));
    const nextPrompt = catalog.find((p) => !used.has(p.id));
    if (!nextPrompt || rows.length >= MAX_PROMPTS) return;
    setRows((prev) => [...prev, { prompt_id: nextPrompt.id, answer: "" }]);
    setDirty(true);
  }

  function removeRow(index: number) {
    setRows((prev) => prev.filter((_, i) => i !== index));
    setDirty(true);
  }

  async function onSave() {
    if (busy) return;
    const filled = rows.filter((r) => r.answer.trim().length > 0);
    setBusy(true);
    try {
      await api.put("/me/prompts", {
        body: filled.map((r, i) => ({ prompt_id: r.prompt_id, answer: r.answer.trim(), position: i + 1 })),
      });
      setDirty(false);
      toast("Prompts saved.", "success");
      onSaved();
    } catch (err) {
      if (isApiError(err)) toast(err.message, "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="prompts-editor" data-testid="prompts-editor">
      {rows.length === 0 && <p className="text-faint">{copy.profile.empty_slots.prompts}</p>}
      {rows.map((row, index) => {
        const takenElsewhere = new Set(rows.filter((_, i) => i !== index).map((r) => r.prompt_id));
        return (
          <div key={index} className="prompts-editor__row">
            <div className="field">
              <label htmlFor={`prompt-select-${index}`}>Prompt {index + 1}</label>
              <select
                id={`prompt-select-${index}`}
                value={row.prompt_id}
                onChange={(e) => updateRow(index, { prompt_id: Number(e.target.value) })}
              >
                {catalog
                  .filter((p) => p.id === row.prompt_id || !takenElsewhere.has(p.id))
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {promptText(p.id)}
                    </option>
                  ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor={`prompt-answer-${index}`}>Your answer</label>
              <textarea
                id={`prompt-answer-${index}`}
                data-testid={`prompt-answer-${index}`}
                maxLength={MAX_ANSWER}
                rows={2}
                value={row.answer}
                onChange={(e) => updateRow(index, { answer: e.target.value })}
              />
            </div>
            <Button size="sm" variant="ghost" onClick={() => removeRow(index)}>
              Remove
            </Button>
          </div>
        );
      })}
      <div className="prompts-editor__actions">
        {rows.length < MAX_PROMPTS && (
          <Button size="sm" variant="ghost" onClick={addRow} disabled={catalog.length === 0}>
            + Add a prompt
          </Button>
        )}
        <Button size="sm" variant="primary" loading={busy} disabled={!dirty} onClick={() => void onSave()}>
          Save prompts
        </Button>
      </div>
    </div>
  );
}
